import {
  Dispatch,
  FormEvent,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { Form, Formik } from 'formik';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormError } from '@/components/ui/form-field';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { Spinner } from '@/components/ui/spinner';
import { Variants } from '@/types/common';
import { PMSVendorData } from '@/types/pms.types';
import type { PmsAccountRef } from '@/components/pms/services/pmsAccount';
import { testVendorConnectionSyncAPI } from '../../../pms/pms-configuration/service/configurationApiService';
import { showAlert } from '../../../../slice/AlertSlice';
import {
  type ConnectionState,
  connectedConnection,
  failedConnection,
} from '../../../pms/pms-configuration/types/connection-state';
import { FormikDirtyReporter } from '@/components/pms/vendor-settings/panels/FormikDirtyReporter';
import { SubmitRefCleanup } from '@/components/pms/vendor-settings/panels/SubmitRefCleanup';
import type { VendorSettingsMode, SubmittedCredentials } from '@/components/pms/vendor-settings/types';

const subdomainSchema = yup.object().shape({
  url: yup
    .string()
    .required('Please enter subdomain')
    .matches(
      /^[a-zA-Z0-9-]+$/,
      'Subdomain may only contain letters, numbers, and hyphens',
    ),
});

interface AppFolioOAuthConnectionProps {
  /** DV-3345: the account every PMS call here names. */
  account: PmsAccountRef;
  connected: boolean;
  setConnected: Dispatch<SetStateAction<boolean>>;
  formSubmit: boolean;
  setConnectionState: Dispatch<SetStateAction<ConnectionState>>;
  onSubmit: (values: PMSVendorData) => void;
  redirectToHowToConfigure: (open: boolean) => void;
  configConnectionFormRef?: MutableRefObject<
    ((e?: FormEvent<HTMLFormElement>) => void) | undefined
  >;
  setVenderListData: (data: PMSVendorData) => void;
  autoSyncAssets?: boolean;
  watermarkedPhotos?: boolean;
  setToggleLoader: Dispatch<SetStateAction<boolean>>;
  pmsSource: string;
  initialVendorData?: PMSVendorData | null;
  /** Spec §5.1: the subdomain form only exists in edit mode. */
  mode: VendorSettingsMode;
  /** Remount key so a discard clears the typed subdomain (spec §5.4). */
  formKey: number;
  /**
   * Credentials from a save that was rejected — the form remounts seeded from
   * them instead of blank, so the retry carries what the user pasted
   * (review finding 1). Null at every other time.
   */
  pendingCredentials: SubmittedCredentials | null;
  onDirtyChange: (dirty: boolean) => void;
}

const AppFolioOAuthConnection = ({
  account,
  connected,
  setConnected,
  formSubmit,
  setConnectionState,
  onSubmit,
  redirectToHowToConfigure,
  configConnectionFormRef,
  setVenderListData,
  autoSyncAssets,
  watermarkedPhotos,
  setToggleLoader,
  pmsSource,
  initialVendorData,
  mode,
  formKey,
  pendingCredentials,
  onDirtyChange,
}: AppFolioOAuthConnectionProps) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [PMSVendorsData, setPMSVendorsData] = useState<PMSVendorData>({
    status: undefined,
    url: '',
    key: '',
    secret: '',
    autoSyncAssets,
    watermarkedPhotos,
  });

  useEffect(() => {
    runConnectionTest();
  }, []);

  const runConnectionTest = async () => {
    setLoading(true);
    setToggleLoader(true);
    try {
      if (initialVendorData) {
        setPMSVendorsData(initialVendorData);
        setVenderListData(initialVendorData);
        try {
          const configResponse = await testVendorConnectionSyncAPI(
            account,
            initialVendorData,
            autoSyncAssets ?? false,
            watermarkedPhotos ?? false,
            pmsSource,
          );
          if (configResponse?.data?.status === 'Success') {
            setConnected(true);
            setConnectionState(connectedConnection());
          }
        } catch (error: unknown) {
          const err = error as { response?: { status?: number } };
          if (err.response?.status) {
            setConnected(false);
            setConnectionState(
              failedConnection(),
            );
            const message = formSubmit
              ? 'Connection failed.  Please check the credentials and try again.'
              : 'Connection failed. Update credentials to resume syncing.';
            dispatch(showAlert({ type: Variants.ERROR, message }));
          }
        }
      }
    } finally {
      setLoading(false);
      setToggleLoader(false);
    }
  };

  const initialFormValues: PMSVendorData = {
    url: pendingCredentials?.url ?? PMSVendorsData?.url ?? '',
    key: '',
    secret: '',
  };

  // Before the spinner: read mode shows the panel's summary, and the on-mount
  // credential test must not paint a large spinner underneath it.
  if (mode === 'read') return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <div className="p-4">
        <Typography variant="heading-md" className="font-medium">
          Please enter your AppFolio subdomain below. Refer to the{' '}
        </Typography>
        <Button
          type="button"
          variant="link"
          onClick={() => redirectToHowToConfigure(true)}
        >
          {' guide '}
        </Button>
        <Typography variant="heading-md" className="font-medium">
          {' '}
          in the Integration Overview tab for step-by-step instructions.
        </Typography>
      </div>
      <Formik
        key={formKey}
        validationSchema={subdomainSchema}
        initialValues={initialFormValues}
        validateOnBlur={true}
        validateOnChange={false}
        onSubmit={async (values) => {
          onSubmit(values);
        }}
        enableReinitialize
      >
        {({ handleSubmit, handleChange, values, touched, errors }) => {
          if (configConnectionFormRef) {
            configConnectionFormRef.current = handleSubmit;
          }
          const showUrlError = Boolean(touched.url && errors.url);
          return (
            <Form id="vendor-configuration" noValidate onSubmit={handleSubmit}>
              <FormikDirtyReporter onDirtyChange={onDirtyChange} />
              {/* The ref prop is optional and stores `undefined`, not `null`. */}
              <SubmitRefCleanup
                clear={() => {
                  if (configConnectionFormRef) configConnectionFormRef.current = undefined;
                }}
              />
              <div
                className={`mb-4 px-4 ${connected ? '' : 'border-destructive border rounded-md p-3'}`}
              >
                <Label htmlFor="appfolio-subdomain" required>
                  AppFolio Subdomain
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="appfolio-subdomain"
                    name="url"
                    type="text"
                    placeholder="Enter Subdomain"
                    value={values.url ?? ''}
                    onChange={handleChange}
                    aria-invalid={showUrlError}
                    aria-describedby="url-suffix"
                    className={`max-w-md ${showUrlError ? 'border-destructive' : ''}`}
                  />
                  <span
                    id="url-suffix"
                    className="text-sm text-muted-foreground"
                  >
                    .appfolio.com
                  </span>
                </div>
                <FormError message={touched.url ? errors.url : undefined} />
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default AppFolioOAuthConnection;
